import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { LANGUAGE_NAMES, languageName, type BotLanguages } from "@kickwin/shared";
import { api } from "@/lib/api";
import { useLanguages } from "@/lib/i18n";
import { useCan } from "@/lib/shell-context";
import { cn } from "@/lib/utils";
import { Button, ErrorNote, Field, Loading, Panel, Select } from "@/components/primitives";

/**
 * Languages (decision 46). The default is the language messages are written
 * in; each extra language gets a tab in every editor, where only the words
 * are translated. A player is answered in their Telegram app's language when
 * the bot has it, and in the default otherwise.
 */
export function LanguagesPage() {
  const { botId } = useParams();
  const saved = useLanguages(botId);
  if (!saved) return <Loading />;
  return <Editor key={JSON.stringify(saved)} botId={botId} saved={saved} />;
}

function Editor({ botId, saved }: { botId: string | undefined; saved: BotLanguages }) {
  const canEdit = useCan("commands", "manage");
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<BotLanguages>(saved);

  const save = useMutation({
    mutationFn: () => api.put<BotLanguages>(`/bots/${botId}/languages`, draft),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["languages", botId] }),
  });

  const toggle = (code: string) =>
    setDraft({
      ...draft,
      extra: draft.extra.includes(code)
        ? draft.extra.filter((c) => c !== code)
        : [...draft.extra, code],
    });
  const changed = JSON.stringify(draft) !== JSON.stringify(saved);

  return (
    <div className="grid max-w-3xl grid-cols-1 gap-4">
      <Panel
        title="Languages"
        description="Players are answered in their Telegram app's language when the bot has it, otherwise in the default."
      >
        <fieldset disabled={!canEdit} className="grid gap-5 px-5 py-4">
          <Field
            label="Default language"
            hint="The language your messages are written in. Changing it does not translate anything; it only names what is already there."
          >
            <Select
              value={draft.default}
              onChange={(e) =>
                setDraft({
                  default: e.target.value,
                  extra: draft.extra.filter((c) => c !== e.target.value),
                })
              }
              className="md:max-w-xs"
            >
              {Object.entries(LANGUAGE_NAMES).map(([code, name]) => (
                <option key={code} value={code}>
                  {name} ({code})
                </option>
              ))}
            </Select>
          </Field>

          <div className="grid gap-2">
            <div className="font-medium">Also answer in</div>
            <p className="text-muted-foreground">
              Each language you tick gets a tab in Auto reply, Commands, Broadcasts and Bottom menu.
              Anything you leave untranslated uses the default.
            </p>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 md:grid-cols-3">
              {Object.entries(LANGUAGE_NAMES)
                .filter(([code]) => code !== draft.default)
                .map(([code, name]) => {
                  const on = draft.extra.includes(code);
                  return (
                    <label
                      key={code}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 transition-colors [@media(pointer:coarse)]:py-2.5",
                        on ? "border-primary bg-accent" : "border-border hover:border-ring/60",
                      )}
                    >
                      <input type="checkbox" checked={on} onChange={() => toggle(code)} />
                      <span className="min-w-0 truncate">{name}</span>
                      <span className="mono ml-auto text-muted-foreground">{code}</span>
                    </label>
                  );
                })}
            </div>
          </div>
        </fieldset>

        {canEdit && (
          <div className="flex flex-wrap items-center gap-3 border-t border-border px-5 py-3">
            <Button disabled={save.isPending || !changed} onClick={() => save.mutate()}>
              {save.isPending ? "Saving" : "Save languages"}
            </Button>
            {!changed && save.isSuccess && (
              <span className="text-ok">
                Saved. Telegram's command menu now follows each player's language.
              </span>
            )}
            {changed && draft.extra.length < saved.extra.length && (
              <span className="text-muted-foreground">
                Translations for a language you untick are kept, in case you tick it again.
              </span>
            )}
          </div>
        )}
        {save.error && (
          <div className="px-5 pb-3">
            <ErrorNote error={save.error} />
          </div>
        )}
      </Panel>

      <Panel title="In use">
        <div className="flex flex-wrap gap-2 px-5 py-4">
          <span className="rounded-full bg-primary px-3 py-1 text-[13px] text-primary-foreground">
            {languageName(saved.default)} · default
          </span>
          {saved.extra.map((code) => (
            <span key={code} className="rounded-full border border-border px-3 py-1 text-[13px]">
              {languageName(code)}
            </span>
          ))}
        </div>
      </Panel>
    </div>
  );
}
